import {
  Ard,
  UseCase,
  UseCaseName,
  ValidateArdCommand,
  ValidateArdCommandInput,
  ValidateArdRequest,
} from "@amzn/fuse-accounting-central-datastore-client";

/**
 * Creates a ValidateArdCommand from the form data
 * @param formData - The ARD form data
 * @returns ValidateArdCommand with logging
 */
export const createValidateArdCommand = (formData: Ard): ValidateArdCommand => {
  // Build the ValidateArdRequest from formData
  const validateArdRequest: ValidateArdRequest = {
    clientId: "Fuse", // Add required clientId
    id: formData.id == "" ? undefined : formData.id,
    product: formData.product,
    template: formData.template,
    term: formData.term,
    billingMode: formData.billingMode,
    businessCountryCode: formData.businessCountryCode,
    buyingCustomerCountryCode: formData.buyingCustomerCountryCode,
    functionalCurrency: formData.functionalCurrency,
    localCurrency: formData.localCurrency,
    usecases: formData.usecases
      ?.filter((useCase: UseCase) => useCase.name !== UseCaseName.OTHER)
      .map((useCase: UseCase) => ({
        name: useCase.name,
        uslLines: useCase.uslLines?.map((uslLine) => ({
          accountNumber: uslLine.accountNumber,
          channel: uslLine.channel,
          companyCode: uslLine.companyCode,
          costCenter: uslLine.costCenter,
          debitOrCredit: uslLine.debitOrCredit,
          glLineDescriptionSuffix: uslLine.glLineDescriptionSuffix,
          location: uslLine.location,
          productLine: uslLine.productLine,
          project: uslLine.project,
        })),
      })),
  };

  // Create the ValidateArdCommand directly with the request
  const validateArdCommand = new ValidateArdCommand(validateArdRequest);

  // Log the command and request details
  logValidateArdCommand(validateArdCommand, "ValidateArdCommand Created");
  logValidateArdRequest(validateArdRequest, "ARD Request Details");

  console.log("Form Data Summary:", {
    ardId: formData.id,
    product: formData.product,
    template: formData.template,
    useCasesCount: formData.usecases?.length || 0,
    totalUslLines:
      formData.usecases?.reduce(
        (total, useCase) => total + (useCase.uslLines?.length || 0),
        0,
      ) || 0,
  });

  return validateArdCommand;
};

/**
 * Logs the ValidateArdCommand in a structured format
 * @param command - The ValidateArdCommand to log
 * @param title - Optional title for the log group
 */
export const logValidateArdCommand = (
  command: ValidateArdCommand,
  title = "ValidateArdCommand",
) => {
  console.group(`${title}`);
  console.log("Command:", command);
  console.log("Input:", command.input);
  console.groupEnd();
};

/**
 * Logs the ValidateArdRequest in a structured format
 * @param request - The ValidateArdRequest to log
 * @param title - Optional title for the log group
 */
export const logValidateArdRequest = (
  request: ValidateArdRequest,
  title = "ValidateArdRequest",
) => {
  console.group(`${title}`);
  console.table({
    "Client ID": request.clientId,
    "ARD ID": request.id,
    Product: request.product,
    Template: request.template,
    Term: request.term,
    "Billing Mode": request.billingMode,
    "Business Country": request.businessCountryCode,
    "Customer Country": request.buyingCustomerCountryCode,
    "Functional Currency": request.functionalCurrency,
    "Local Currency": request.localCurrency,
  });

  if (request.usecases) {
    console.log("Use Cases & USL Lines:");
    request.usecases.forEach((useCase: any, ucIndex: number) => {
      console.group(`Use Case ${ucIndex + 1}: ${useCase.name}`);
      if (useCase.uslLines) {
        console.table(
          useCase.uslLines.map((line: any, lineIndex: number) => ({
            "Line #": lineIndex + 1,
            "Account Number": line.accountNumber || "",
            Location: line.location || "",
            "Debit/Credit": line.debitOrCredit || "",
            "Cost Center": line.costCenter || "",
            "Product Line": line.productLine || "",
            Project: line.project || "",
            Channel: line.channel || "",
            "Company Code": line.companyCode || "",
          })),
        );
      }
      console.groupEnd();
    });
  }

  console.groupEnd();
};
